import { PlatformClient } from "@hololive/browser-sdk/platform";

export type RenewToken = () => Promise<string | null>;

export function createRefreshingFetch(renewToken: RenewToken, fetcher: typeof globalThis.fetch = globalThis.fetch): typeof globalThis.fetch {
  return async (input, init) => {
    const response = await fetcher(input, init);
    const headers = new Headers(init?.headers);
    if (response.status !== 401 || !headers.get("Authorization")?.startsWith("Bearer ")) return response;

    const token = await renewToken();
    if (!token) return response;

    await response.body?.cancel();
    headers.set("Authorization", `Bearer ${token}`);
    return fetcher(input, { ...init, headers });
  };
}

export function createPlatformClient(token: string, domainId: string, renewToken: RenewToken): PlatformClient {
  return new PlatformClient({
    base_url: import.meta.env.VITE_PLATFORM_URL || window.location.origin,
    token,
    ...(domainId.trim() ? { domain_id: domainId.trim() } : {}),
    timeout_ms: 15_000,
    retry_budget: 2,
    fetch: createRefreshingFetch(renewToken),
  });
}

export function idempotencyKey(prefix: string, stableKey?: string): string {
  const storageKey = `hl-web-demo.idempotency.${prefix}.${stableKey || "active"}`;
  const existing = window.sessionStorage.getItem(storageKey);
  if (existing) return existing;
  const value = `demo-${prefix}-${crypto.randomUUID()}`;
  window.sessionStorage.setItem(storageKey, value);
  return value;
}

export function redact(value: unknown): unknown {
  if (typeof value === "string") {
    if (value.startsWith("eyJ") || value.length > 180 && (value.startsWith("http") || value.includes("token"))) return "[redacted]";
    return value;
  }
  if (Array.isArray(value)) return value.map(redact);
  if (value && typeof value === "object") {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [
      key.toLowerCase().includes("token") || key.toLowerCase().includes("credential") || key.toLowerCase().includes("password") || key === "url" ? key : key,
      key.toLowerCase().includes("token") || key.toLowerCase().includes("credential") || key.toLowerCase().includes("password") || key === "url" ? "[redacted]" : redact(item),
    ]));
  }
  return value;
}

export function formatError(error: unknown): string {
  if (error && typeof error === "object") {
    const item = error as { code?: string; status?: number; message?: string; trace_id?: string };
    return [item.code, item.status && `HTTP ${item.status}`, item.message, item.trace_id && `trace=${item.trace_id}`].filter(Boolean).join(" · ");
  }
  return String(error);
}
